import React from "react"
import * as Yup from 'yup'
import { Button } from "@mui/material"
import { format, isValid } from "date-fns"
import { store } from "../../../stores/Store"
import Field from "../../form/fields/Field"
import { Icons } from "../../../misc/Icons"
import Ajax from "../../../Ajax"
import { Theme } from "../../../misc/Theme"
import { useFormik } from "formik"
import Help from "../../../misc/Help"
import InlineMenu from "../../../misc/InlineMenu"
import { UTCDate } from "@date-fns/utc"
import { Alert, AlertModal } from "../../.."
import QuestionnaireCopyForm from "./QuestionnaireCopyForm"
import { observer } from "mobx-react-lite"


export default observer(function QuestionnaireDetails () {
    
    const { questionnaire } = store.QuestionnaireStore
    const [shortlivedAccessUriIsValid, setShortlivedAccessUriIsValid] = React.useState(true)
    
    var updatedAt = new UTCDate(questionnaire.updatedAt)
    var updatedAtDisplay = isValid(updatedAt) ? format(updatedAt, "yyyy-MM-dd HH:mm") : updatedAt.toString()

    var createdAt = new UTCDate(questionnaire.createdAt)
    var createdAtDisplay = isValid(createdAt) ? format(createdAt, "yyyy-MM-dd HH:mm") : createdAt.toString()

    const SponsorHelp = () => {
        return (
            <Help content={<Icons.Info style={{fontSize: "12px", color: Theme.palette.primary.main, marginLeft: "3px"}} />}>
                Enables filtering and allows authorised sponsor users to view this questionnaire.
            </Help>
        )
    }

    return (
        <div className="form">
            <div style={{display: "grid", gap: "30px"}}>
                <div style={{display: "grid", gridTemplateColumns: "150px auto", alignItems: "center", gap: "10px"}}> 
                    <span className="font-grey">Reference/label</span>
                    <span><QuestionnaireName questionnaire={questionnaire} /></span>
                    
                    {!store.AppStore.checkJwtRoles(["client_account", "sponsor_account"], false) ? (
                        <>
                            <span className="font-grey" style={{display: "flex"}}>Sponsor <SponsorHelp /></span>
                            <span><QuestionnaireAdvisor questionnaire={questionnaire} /></span>
                        </>
                    ) : null}
                    
                    <span className="font-grey">Questionnaire</span>
                    <span>{questionnaire.template.displayName} <span style={{color: "lightgrey"}}>{questionnaire.template.version}</span></span>
                    
                    <span className="font-grey">Advisor email</span>
                    {questionnaire.notificationEmail
                        ? <span>{questionnaire.notificationEmail}</span>
                        : <span>No advisor email set up for notifications.</span>}
                    
                    <span className="font-grey">Respondent email</span>
                    <span>{questionnaire.respondent.respondentEmail}</span>
                    
                    <span className="font-grey">Created at</span>
                    <span>
                        {createdAtDisplay}
                    </span>
                    
                    <span className="font-grey">Last updated</span>
                    <span>
                        {updatedAtDisplay}{questionnaire.percentageComplete ? ` - ${questionnaire.percentageComplete}% complete.` : null}
                    </span>
                </div>
                
                <div style={{display: "grid", gap: "5px"}}>
                    <h4>Accessing this Questionnaire</h4>

                    <p>This Questionnaire can be accessed using the following link and access code.</p>

                    <span style={{display: "flex", gap: "5px", alignItems: "center", marginLeft: "15px"}}>
                        <a 
                            className="link" 
                            rel="noreferrer"
                            target="_blank"
                            href={questionnaire.link}>
                            {questionnaire.link}
                        </a>

                        {/* <Button className="btn btn-xs" color="primary" variant="contained" onClick={() => navigator.clipboard.writeText(questionnaire.link)}>Copy</Button> */}
                    </span>
                    <span style={{display: "flex", alignItems: "center", marginLeft: "15px", gap: "10px", fontFamily: "monospace", color: "#484848"}}>
                        <b>{questionnaire.accessCode}</b> 
                        <Button 
                            className="btn btn-sm" 
                            variant="text" 
                            onClick={() => navigator.clipboard.writeText(questionnaire.accessCode)}>
                            <Icons.Copy />
                            Copy access code
                        </Button>
                    </span>

                    <p>Use the 'Open' button for direct and secure access via AORA.</p>
                </div>
            </div> 

            <div className="form-buttons">
                <Button
                    className="btn btn-sm"
                    onClick={() => store.AlertStore.Close()}>
                    Close
                </Button>

                <div className="form-buttons">
                    {store.AppStore.checkJwtRole("account_manager") ? (
                        <Delete questionnaire={questionnaire} />
                    ) : null}

                    <LockToggler questionnaire={questionnaire} />
                    
                    {store.AppStore.checkJwtRole("sysuser") ? (
                        <Button
                            onClick={async () => await Ajax.Questionnaire.Export(questionnaire.uid)}
                            className="btn btn-sm" 
                            color="secondary"
                            variant="contained">
                            Export
                        </Button>
                    ) : null}

                    <Button
                        onClick={() => AlertModal({body: <QuestionnaireCopyForm questionnaire={questionnaire} />})}
                        className="btn btn-sm" 
                        color="secondary"
                        variant="contained">
                        Make a copy
                    </Button>

                    <a 
                        href={`${questionnaire.shortlivedAccessUri}?isPrivilegedUser=true&completionRedirect=${window.location}`} 
                        className={shortlivedAccessUriIsValid ? "" : "disabled"} 
                        target="_blank" 
                        onClick={async () => {
                            setShortlivedAccessUriIsValid(false)
                            await store.QuestionnaireStore.getQuestionnaire(questionnaire.uid)
                            setShortlivedAccessUriIsValid(true)
                        }}>
                        <Button
                            className="btn btn-sm" 
                            color="success"
                            disabled={!shortlivedAccessUriIsValid}
                            variant="contained">
                            <Icons.Open /> Open
                        </Button>
                    </a>
                </div>
            </div>           
        </div>
    )
})


function Delete ({questionnaire}) {

    const [submitting, setSubmitting] = React.useState(false)

    const confirmDelete = () => {
        AlertModal({body: (
            <div className="form">
                <div className="form-fields">
                    <h4>Are you sure you want to permanently delete this questionnaire?</h4>
                </div>

                <div className="form-buttons">
                    <Button 
                        className="btn btn-sm" 
                        variant="contained"
                        onClick={() => store.AlertStore.Close()}>
                        Cancel
                    </Button>
                    <Button  
                        className="btn btn-sm" 
                        variant="contained" 
                        color="error" 
                        onClick={async () => {
                            store.AlertStore.Close()
                            setSubmitting(true)
                            await Ajax.Questionnaire.Delete(questionnaire.uid).then(() => {
                                store.QuestionnaireStore.getQuestionnaires()
                                store.AlertStore.Close()
                            }).catch((response) => {
                                Alert({message: response.data?.detail ? response.data.detail : "Couldn't delete questionnaire"})
                            })
                            setSubmitting(false)
                    }}>
                        Confirm and delete
                    </Button>
                </div>
            </div>
        )})
    }

    return (
        <div>
            <Button 
                disabled={submitting} 
                className="btn btn-sm" 
                variant="contained" 
                color="error" 
                onClick={async (e) => {
                    confirmDelete()
                }}>
                Delete
            </Button>
        </div>
    )
}


function LockToggler ({questionnaire}) {

    const [submitting, setSubmitting] = React.useState(false)

    const toggleLock = async () => {
    
        var updatedQuestionnaire = {
            uid: questionnaire.uid,
            locked: !questionnaire.locked
        }

        await Ajax.Questionnaire.Update(updatedQuestionnaire).then(async () => {
            await store.QuestionnaireStore.getQuestionnaire(questionnaire.uid)
        }).catch((response) => {
            Alert({message: response.data?.detail ? response.data.detail : "Couldn't update questionnaire"})
        })
    }

    return (
        <Button
            className="btn btn-sm"
            variant="contained" 
            disabled={submitting}
            color="secondary"
            onClick={async (e) => {
                setSubmitting(true)
                await toggleLock()
                setSubmitting(false)
            }}>
            {questionnaire.locked ? "Unlock" : "Lock"}
        </Button>
    )
}


function QuestionnaireName ({questionnaire}) {

    const [success, setSuccess] = React.useState(null)

    var formik = useFormik({

        initialValues: {
            uid: questionnaire.uid,
            displayName: questionnaire.displayName
        },

        validationSchema: Yup.object({
            displayName: Yup.string().matches(/^[ ,.@!$%#';:?()[\]~_+=À-Ö\p{Sc}\p{So}\p{Mn}\p{P}\p{Z}À-ÿ\w\n]*$/u, "You have used some illegal characters").required("You must prvide a name")
        }),

        onSubmit: async (values) => {

            var newQuestionnaire = {...questionnaire, displayName: values.displayName}

            await Ajax.Questionnaire.Update(newQuestionnaire).then(async () => {
                setSuccess(true)
                questionnaire.displayName = values.displayName
                store.QuestionnaireStore.getQuestionnaires()
            }).catch((response) => {
                setSuccess(false)
            })
        }
    })

    return (
        <form style={{width: "252px", display: "grid", gridTemplateColumns: "auto 35px"}} onSubmit={formik.handleSubmit}>
            <input
                name="displayName" 
                className="native-input"
                value={formik.values.displayName} 
                onChange={(e) => {
                    formik.handleChange(e)
                    setSuccess(null)
                }} />


            {formik.values.displayName !== questionnaire.displayName ? (
                <Button 
                    color="success"
                    variant="contained"
                    className="btn btn-sm" 
                    disabled={formik.isSubmitting}
                    type="submit">
                    {success ? "Saved" : "Save"}
                </Button>
            ) : null}
        </form>
    )
}


function QuestionnaireAdvisor ({questionnaire}) {

    // const [value, setValue] = React.useState(questionnaire.respondent.advisorUid)
    const [success, setSuccess] = React.useState(null)

    const userAccount = store.NodeStore.accounts.find((a) => a.uid === store.AppStore.sessionInfo.userAccount.uid)

    var formik = useFormik({

        initialValues: {
            uid: questionnaire.uid,
            advisorUid: questionnaire.respondent.advisorUid
        },

        validationSchema: Yup.object({
            advisorUid: Yup.string().required()
        }),

        onSubmit: async (values) => {
           
            var newQuestionnaire = {...questionnaire}
            newQuestionnaire.respondent.advisorUid = values.advisorUid
           
            await Ajax.Questionnaire.Update(newQuestionnaire).then(async () => {
                setSuccess(true)
                questionnaire.respondent.advisor = values.advisorUid
                store.QuestionnaireStore.getQuestionnaires()
            }).catch((response) => {
                setSuccess(false)
            })
        }
    })

    const advisorAccount = store.NodeStore.accounts.find(a => a.advisorUid === formik.values.advisorUid)
    const selectedLabel = `${advisorAccount?.label} ${["customer", "admin"].includes(advisorAccount.accountType) ? "(Unassigned)" : ""}`

    return (
        <form style={{width: "210px", display: "grid", gridTemplateColumns: "auto 35px"}} onSubmit={formik.handleSubmit}>
            <InlineMenu opener={(
                <div style={{width: "218px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "0.5px solid grey"}}>
                    <span className="trunc-ellipsis">{selectedLabel}</span>
                    <Icons.ChevronLeft style={{fontSize: "18px", transform: "rotateZ(-90deg)", color: "grey"}} />
                </div>
            )}>
                <div>
                    <div style={{padding: "15px"}}>
                        <h3>Assign the Questionnaire to a Sponsor</h3>    
                    </div>
                    
                    <div style={{padding: "0 15px 15px 15px", maxHeight: "300px", overflowY: "auto"}}>
                        <Field.CheckboxSelect
                            formik
                            multiple={false}
                            name="advisorUid" 
                            value={formik.values.advisorUid}
                            options={store.QuestionnaireStore.advisorOptions}
                            warning={false}
                            onChange={(e) => {
                                formik.handleChange(e)
                                setSuccess(null)
                            }} />
                    </div>
                </div>
            </InlineMenu>

            {formik.values.advisorUid !== questionnaire.respondent.advisorUid ? (
                <Button 
                    color="success"
                    variant="contained"
                    className="btn btn-sm" 
                    disabled={formik.isSubmitting}
                    type="submit">
                    {success ? "Saved" : "Save"}
                </Button>
            ) : null}
        </form>
    )
}